import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Pagination,
  FormHelperText,
  Card,
  CardContent,
  Grid,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Switch,
  Tooltip
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  CameraAlt as CameraIcon,
  PhotoLibrary as PhotoLibraryIcon,
  ExpandMore as ExpandMoreIcon,
  Check as CheckIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { usePermissions } from '../hooks/usePermissions';
import purchaseOrdersApi, {
  CreatePurchaseOrderDTO,
  PurchaseOrder,
  PurchaseOrderItem,
  PurchaseOrderStatus,
  POItemStatus
} from '../api/purchaseOrders';
import { MaterialType } from '../api/materials';
import { MATERIAL_UOMS } from '../constants/materials';
import vendorApi, { Vendor } from '../api/vendor';
import api, { getMaterialQualities } from '../services/api';
import instance from '../utils/axios';
import { format, addDays } from 'date-fns';
import { generatePurchaseOrderPdf } from '../utils/pdfGenerator';
import { useAuth } from '../contexts/AuthContext';

// Extend PurchaseOrder locally to include design_tags
interface PurchaseOrderWithDesigns extends PurchaseOrder {
  design_tags?: { id: number; design_no: string }[];
}

interface PurchaseOrderItemWithImage extends PurchaseOrderItem {
  image_url?: string;
  imageFile?: File | null;
}

const emptyItem: PurchaseOrderItemWithImage = {
  material_type: MaterialType.FABRIC,
  quantity: 0,
  uom: 'mtr',
  rate: 0,
  amount: 0,
  material_quality: '',
  description: '',
  is_sample: false,
  garment_part: null,
  image_url: '',
  imageFile: null
};

const PurchaseOrdersPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { canView, canAdd, canEdit, canDelete } = usePermissions();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrderWithDesigns[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [showCancelled, setShowCancelled] = useState(false);

  // Filter state
  const [filterDesignNo, setFilterDesignNo] = useState('');
  const [filterVendorId, setFilterVendorId] = useState<number | ''>('');
  const [filterStatus, setFilterStatus] = useState<PurchaseOrderStatus | ''>('');

  const [vendors, setVendors] = useState<Vendor[]>([]);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [formData, setFormData] = useState<CreatePurchaseOrderDTO & { items: PurchaseOrderItemWithImage[]; design_nos: string[] }>({
    vendor_id: 0,
    notes: '',
    terms: '',
    valid_until: format(addDays(new Date(), 45), 'yyyy-MM-dd'),
    items: [{ ...emptyItem }],
    design_nos: []
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [uploadingImages, setUploadingImages] = useState<{ [key: number]: boolean }>({});
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState<PurchaseOrderWithDesigns | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  // Track per-item status edits in the view dialog: itemId → new status
  const [itemStatusEdits, setItemStatusEdits] = useState<Record<number, POItemStatus>>({});
  const [savingItemStatuses, setSavingItemStatuses] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [poToDelete, setPoToDelete] = useState<PurchaseOrderWithDesigns | null>(null);
  const [poEditMode, setPoEditMode] = useState<'create' | 'edit'>('create');
  const [editingPoId, setEditingPoId] = useState<number | null>(null);
  const [materialQualityOptions, setMaterialQualityOptions] = useState<string[]>([]);

  // Design tag state — store full objects to show thumbnails
  interface DesignOption { design_no: string; name?: string; image_url?: string | null; }
  const [designTagOptions, setDesignTagOptions] = useState<DesignOption[]>([]);
  const [designTagsLoading, setDesignTagsLoading] = useState(false);
  const designTagTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Vendor search state
  const [vendorInputValue, setVendorInputValue] = useState('');

  // Camera/photo capture states
  const [captureDialogOpen, setCaptureDialogOpen] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState<number | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const fileInputRefs = useRef<{ [key: number]: HTMLInputElement | null }>({});

  const canViewPurchaseOrders = useMemo(() => canView('purchase-orders'), [canView]);
  const canAddPurchaseOrders = useMemo(() => canAdd('purchase-orders'), [canAdd]);
  const canEditPurchaseOrders = useMemo(() => canEdit('purchase-orders'), [canEdit]);
  const canDeletePurchaseOrders = useMemo(() => canDelete('purchase-orders'), [canDelete]);

  // URL param handling: auto-open add dialog on mount if ?new=1
  // Supports single design: ?new=1&design_no=D001
  // Supports multiple designs: ?new=1&design_nos=D001,D002,D003
  // Supports garment_part pre-fill: ?new=1&design_no=D001&garment_part=DUPATTA
  useEffect(() => {
    const isNew = searchParams.get('new');
    const designNo = searchParams.get('design_no');        // single
    const designNosParam = searchParams.get('design_nos'); // comma-separated bulk
    const garmentPartParam = searchParams.get('garment_part'); // e.g. DUPATTA
    if (isNew === '1') {
      let initialDesigns: string[] = [];
      if (designNosParam) {
        initialDesigns = designNosParam.split(',').map(s => s.trim()).filter(Boolean);
      } else if (designNo) {
        initialDesigns = [designNo];
      }
      // Pre-fill garment_part on the default empty item if provided via URL
      const seedItem: PurchaseOrderItemWithImage = {
        ...emptyItem,
        garment_part: garmentPartParam || null,
      };
      setFormData(prev => ({ ...prev, design_nos: initialDesigns, items: [seedItem] }));
      setAddDialogOpen(true);
      setPoEditMode('create');
      setSearchParams({}, { replace: true });
      if (initialDesigns.length > 0) {
        // Immediately seed options with stub entries so the chips render right away
        setDesignTagOptions(initialDesigns.map(d => ({ design_no: d })));
        // Then fetch full details (with image_url) for all seeded designs
        seedDesignTagOptions(initialDesigns);
      }
    }
  }, []); // run once on mount

  // Fetch full design details for a set of design_nos and merge into options
  const seedDesignTagOptions = async (designNos: string[]) => {
    try {
      // Fetch each by its design_no as a search query (they're usually unique prefixes)
      // Use a broad search with no approval filter so any status is found
      const results = await Promise.all(
        designNos.map(dn =>
          instance.get('/designs', {
            params: { summary: 1, page: 1, pageSize: 10, q: dn }
          }).then(r => {
            const data: any[] = Array.isArray(r.data) ? r.data : (r.data?.data || []);
            // Find the exact match by design_no
            return data.find((d: any) => d.design_no === dn) || null;
          }).catch(() => null)
        )
      );
      const enriched = designNos.map((dn, i) => {
        const found = results[i];
        return found
          ? { design_no: found.design_no, name: found.name, image_url: found.image_url }
          : { design_no: dn };
      });
      setDesignTagOptions(enriched);
    } catch { /* silent - stubs already in place */ }
  };

  const loadDesignTagOptions = async (q: string) => {
    setDesignTagsLoading(true);
    try {
      const resp = await instance.get('/designs', {
        params: { summary: 1, page: 1, pageSize: 50, q: q || undefined, approval_status: 'APPROVED' }
      });
      const data: any[] = Array.isArray(resp.data) ? resp.data : (resp.data?.data || []);
      setDesignTagOptions(
        data
          .filter((d: any) => d.approval_status === 'APPROVED')
          .map((d: any) => ({ design_no: d.design_no, name: d.name, image_url: d.image_url } as any))
      );
    } catch { /* silent */ }
    finally { setDesignTagsLoading(false); }
  };

  const fetchVendors = async () => {
    try {
      // Only load SUPPLIER type vendors for Purchase Orders
      const response = await vendorApi.getAll({ vendor_type: 'SUPPLIER' });
      setVendors(response.data);
    } catch (err) {
      console.error('Error fetching vendors for purchase orders:', err);
    }
  };

  const fetchPurchaseOrders = async (
    pageNum = 1,
    pageSizeNum = 10,
    q?: string,
    includeCancelled?: boolean,
    vendorId?: number | '',
    status?: PurchaseOrderStatus | '',
    designNo?: string
  ) => {
    try {
      setLoading(true);
      setError(null);
      const response = await purchaseOrdersApi.getAll({
        page: pageNum,
        pageSize: pageSizeNum,
        q,
        include_cancelled: includeCancelled,
        vendor_id: vendorId || undefined,
        status: status || undefined,
        design_no: designNo?.trim() || undefined
      });
      const data = response.data;

      if (Array.isArray(data)) {
        setPurchaseOrders(data as PurchaseOrderWithDesigns[]);
        setTotal(data.length);
        setPage(1);
        setPageSize(data.length || 10);
      } else {
        setPurchaseOrders(data.data as PurchaseOrderWithDesigns[]);
        setTotal(data.total);
        setPage(data.page);
        setPageSize(data.pageSize);
      }
    } catch (err) {
      console.error('Error fetching purchase orders:', err);
      setError('Failed to load purchase orders. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canViewPurchaseOrders) return;
    fetchVendors();
  }, [canViewPurchaseOrders]);

  // Reset to page 1 when filters/search change
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, showCancelled, filterVendorId, filterStatus, filterDesignNo]);

  // Refetch POs when page, pageSize, searchTerm, showCancelled, or filters change
  useEffect(() => {
    if (!canViewPurchaseOrders) return;
    fetchPurchaseOrders(
      page, pageSize,
      searchTerm.trim() || undefined,
      showCancelled,
      filterVendorId,
      filterStatus,
      filterDesignNo
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewPurchaseOrders, page, pageSize, searchTerm, showCancelled, filterVendorId, filterStatus, filterDesignNo]);

  const handleOpenAddDialog = () => {
    setPoEditMode('create');
    setEditingPoId(null);
    setFormData({
      vendor_id: 0,
      notes: '',
      terms: '',
      valid_until: format(addDays(new Date(), 45), 'yyyy-MM-dd'),
      items: [{ ...emptyItem }],
      design_nos: []
    });
    setFormErrors({});
    setAddDialogOpen(true);
    loadDesignTagOptions('');
  };

  // Load dynamic material quality suggestions from backend
  useEffect(() => {
    const fetchMaterialQualitiesFromSettings = async () => {
      try {
        const response = await getMaterialQualities();
        const fromDb = Array.isArray(response.data) ? response.data : [];
        const names = fromDb
          .filter((q: any) => q.isActive)
          .map((q: any) => q.name as string)
          .sort((a: string, b: string) => a.localeCompare(b));
        setMaterialQualityOptions(names);
      } catch (err) {
        console.error('Error fetching material quality suggestions:', err);
      }
    };

    fetchMaterialQualitiesFromSettings();
  }, []);

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setFormData(prev => ({ ...prev, design_nos: [] }));
  };

  const handleFormChange = (field: keyof CreatePurchaseOrderDTO, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleItemChange = (index: number, field: keyof PurchaseOrderItemWithImage, value: any) => {
    setFormData(prev => {
      const items = [...prev.items];
      const updatedItem: PurchaseOrderItemWithImage = {
        ...items[index],
        [field]: value
      };

      // Auto-calculate amount if quantity or rate changes
      if (field === 'quantity' || field === 'rate') {
        const qty = Number(field === 'quantity' ? value : updatedItem.quantity || 0);
        const rate = Number(field === 'rate' ? value : updatedItem.rate || 0);
        updatedItem.amount = Number.isFinite(qty * rate) ? qty * rate : 0;
      }

      items[index] = updatedItem;
      return {
        ...prev,
        items
      };
    });
  };

  const handleAddItemRow = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { ...emptyItem }]
    }));
  };

  const handleRemoveItemRow = (index: number) => {
    if (formData.items.length <= 1) return; // Keep at least one item
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Photo/Camera handling
  const handleOpenCamera = async (itemIndex: number) => {
    setCurrentItemIndex(itemIndex);
    setCaptureDialogOpen(true);

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Unable to access camera. Please check permissions.');
      setCaptureDialogOpen(false);
    }
  };

  const handleCloseCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCaptureDialogOpen(false);
    setCurrentItemIndex(null);
  };

  const handleCapturePhoto = () => {
    if (!videoRef.current || currentItemIndex === null) return;

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(videoRef.current, 0, 0);
      canvas.toBlob(async (blob) => {
        if (blob) {
          const file = new File([blob], `po-item-${Date.now()}.jpg`, { type: 'image/jpeg' });
          await uploadImage(currentItemIndex, file);
        }
      }, 'image/jpeg', 0.9);
    }
    handleCloseCamera();
  };

  const handleFileSelect = async (itemIndex: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await uploadImage(itemIndex, file);
    }
  };

  const uploadImage = async (itemIndex: number, file: File) => {
    try {
      setUploadingImages(prev => ({ ...prev, [itemIndex]: true }));

      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await api.post('/upload?module=purchase-orders', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const imageUrl = response.data.url;
      handleItemChange(itemIndex, 'image_url', imageUrl);
      setSuccess('Image uploaded successfully');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      console.error('Error uploading image:', err);
      setError('Failed to upload image. Please try again.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setUploadingImages(prev => ({ ...prev, [itemIndex]: false }));
    }
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};

    if (!formData.vendor_id) {
      errors.vendor_id = 'Vendor is required';
    }

    if (formData.items.length === 0) {
      errors.items = 'At least one item is required';
    }

    formData.items.forEach((item, index) => {
      if (!item.material_type) {
        errors[`item_${index}_material_type`] = 'Material type is required';
      }
      if (!item.quantity || item.quantity <= 0) {
        errors[`item_${index}_quantity`] = 'Valid quantity is required';
      }
      if (!item.uom) {
        errors[`item_${index}_uom`] = 'UOM is required';
      }
      if (!item.rate || item.rate <= 0) {
        errors[`item_${index}_rate`] = 'Valid rate is required';
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError(null);

      const payload = {
        vendor_id: formData.vendor_id,
        design_nos: formData.design_nos || [],
        notes: formData.notes || undefined,
        terms: formData.terms || undefined,
        valid_until: formData.valid_until || undefined,
        items: formData.items.map((item) => { const { imageFile, ...rest } = item as any; return rest; })
      };

      if (poEditMode === 'edit' && editingPoId) {
        await purchaseOrdersApi.update(editingPoId, payload as CreatePurchaseOrderDTO);
        setSuccess('Purchase order updated successfully!');
      } else {
        await purchaseOrdersApi.create(payload as CreatePurchaseOrderDTO);
        setSuccess('Purchase order created successfully!');
      }

      setTimeout(() => setSuccess(null), 3000);
      handleCloseAddDialog();
      fetchPurchaseOrders(page, pageSize);
    } catch (err: any) {
      console.error('Error saving purchase order:', err);
      setError(err.response?.data?.error || 'Failed to save purchase order');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: PurchaseOrderStatus): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case 'DRAFT': return 'default';
      case 'SENT': return 'info';
      case 'PARTIALLY_FULFILLED': return 'warning';
      case 'FULFILLED': return 'success';
      case 'CLOSED': return 'secondary';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  const handleViewPurchaseOrder = async (poId: number) => {
    try {
      setViewDialogOpen(true);
      setViewLoading(true);
      setItemStatusEdits({});
      const response = await purchaseOrdersApi.getById(poId);
      setSelectedPurchaseOrder(response.data as PurchaseOrderWithDesigns);
    } catch (err) {
      console.error('Error fetching purchase order details:', err);
      setError('Failed to load purchase order details');
      setTimeout(() => setError(null), 3000);
      setViewDialogOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedPurchaseOrder(null);
    setItemStatusEdits({});
  };

  const handleItemStatusEdit = (itemId: number, newStatus: POItemStatus) => {
    setItemStatusEdits(prev => ({ ...prev, [itemId]: newStatus }));
  };

  const handleSaveItemStatuses = async () => {
    if (!selectedPurchaseOrder || Object.keys(itemStatusEdits).length === 0) return;
    try {
      setSavingItemStatuses(true);
      const itemsPayload = Object.entries(itemStatusEdits).map(([id, status]) => ({
        id: Number(id),
        status
      }));
      const response = await purchaseOrdersApi.updateItemStatuses(selectedPurchaseOrder.id, itemsPayload);
      setSelectedPurchaseOrder(response.data as PurchaseOrderWithDesigns);
      setItemStatusEdits({});
      setSuccess('Item statuses updated successfully');
      setTimeout(() => setSuccess(null), 3000);
      // Refresh the list so PO status chip updates too
      fetchPurchaseOrders(page, pageSize, searchTerm.trim() || undefined, showCancelled, filterVendorId, filterStatus, filterDesignNo);
    } catch (err: any) {
      console.error('Error saving item statuses:', err);
      setError(err.response?.data?.error || 'Failed to update item statuses');
      setTimeout(() => setError(null), 3000);
    } finally {
      setSavingItemStatuses(false);
    }
  };

  const buildCompanyDetails = () => ({
    brandName: user?.company?.brand_name,
    name: user?.company?.name,
    address: user?.company?.address,
    contactNumber: user?.company?.contact_number,
  });

  // Fetch image_url for each design tag so the PDF can render thumbnails
  const enrichDesignTagsWithImages = async (
    tags: { id?: number; design_no: string }[]
  ): Promise<{ design_no: string; image_url?: string | null }[]> => {
    if (!tags || tags.length === 0) return [];
    const enriched = await Promise.all(
      tags.map(async tag => {
        try {
          const resp = await instance.get('/designs', {
            params: { summary: 1, page: 1, pageSize: 10, q: tag.design_no }
          });
          const data: any[] = Array.isArray(resp.data) ? resp.data : (resp.data?.data || []);
          const match = data.find((d: any) => d.design_no === tag.design_no);
          return { design_no: tag.design_no, image_url: match?.image_url || null };
        } catch {
          return { design_no: tag.design_no, image_url: null };
        }
      })
    );
    return enriched;
  };

  const handlePrintPurchaseOrder = async () => {
    if (!selectedPurchaseOrder) return;
    try {
      const enrichedTags = await enrichDesignTagsWithImages(selectedPurchaseOrder.design_tags || []);
      await generatePurchaseOrderPdf({
        ...selectedPurchaseOrder,
        design_tags: enrichedTags,
        companyDetails: buildCompanyDetails(),
      });
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Failed to generate PDF');
      setTimeout(() => setError(null), 3000);
    }
  };

  // Print directly from a grid row (fetches full PO data first)
  const handlePrintPOById = async (poId: number) => {
    try {
      const response = await purchaseOrdersApi.getById(poId);
      const po = response.data as PurchaseOrderWithDesigns;
      const enrichedTags = await enrichDesignTagsWithImages(po.design_tags || []);
      await generatePurchaseOrderPdf({
        ...po,
        design_tags: enrichedTags,
        companyDetails: buildCompanyDetails(),
      });
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Failed to generate PDF');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleStatusChange = async (newStatus: PurchaseOrderStatus) => {
    if (!selectedPurchaseOrder) return;

    try {
      setLoading(true);
      await purchaseOrdersApi.updateStatus(selectedPurchaseOrder.id, newStatus);
      setSuccess('Status updated successfully');
      setTimeout(() => setSuccess(null), 3000);

      // Refresh the PO details
      const response = await purchaseOrdersApi.getById(selectedPurchaseOrder.id);
      setSelectedPurchaseOrder(response.data as PurchaseOrderWithDesigns);

      // Refresh the list
      fetchPurchaseOrders(page, pageSize);
    } catch (err: any) {
      console.error('Error updating status:', err);
      setError(err.response?.data?.error || 'Failed to update status');
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditDialog = (po: PurchaseOrderWithDesigns) => {
    setPoEditMode('edit');
    setEditingPoId(po.id);
    setFormData({
      vendor_id: po.vendor_id,
      notes: po.notes || '',
      terms: po.terms || '',
      valid_until: po.valid_until ? format(new Date(po.valid_until), 'yyyy-MM-dd') : '',
      items: (po.items || []).map(item => ({
        material_type: item.material_type,
        material_quality: item.material_quality || '',
        description: item.description || '',
        quantity: item.quantity,
        uom: item.uom,
        rate: item.rate,
        amount: item.amount,
        image_url: item.image_url || '',
        fabric_width: item.fabric_width ?? null,
        fabric_L: item.fabric_L ?? null,
        is_sample: item.is_sample ?? false,
        garment_part: item.garment_part ?? null,
        id: item.id,
        imageFile: null
      })),
      design_nos: (po.design_tags || []).map(t => t.design_no)
    });
    setFormErrors({});
    setAddDialogOpen(true);
    loadDesignTagOptions('');
  };

  const handleDeleteClick = (po: PurchaseOrderWithDesigns) => {
    setPoToDelete(po);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!poToDelete) return;

    try {
      setLoading(true);
      await purchaseOrdersApi.delete(poToDelete.id);
      setSuccess('Purchase order deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
      setDeleteDialogOpen(false);
      setPoToDelete(null);
      fetchPurchaseOrders(page, pageSize);
    } catch (err: any) {
      console.error('Error deleting purchase order:', err);
      setError(err.response?.data?.error || 'Failed to delete purchase order');
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPoToDelete(null);
  };

  // Compute statistics for pending POs (excluding cancelled and fulfilled/closed)
  const stats = useMemo(() => {
    const pendingStatuses: PurchaseOrderStatus[] = [
      PurchaseOrderStatus.DRAFT,
      PurchaseOrderStatus.SENT,
      PurchaseOrderStatus.PARTIALLY_FULFILLED
    ];

    const pendingPOs = purchaseOrders.filter(po =>
      pendingStatuses.includes(po.status)
    );

    let pendingItems = 0;
    let pendingQty = 0;
    let pendingAmount = 0;

    pendingPOs.forEach(po => {
      po.items?.forEach(item => {
        const fulfilled = item.fulfilled_qty || 0;
        const remaining = Math.max(item.quantity - fulfilled, 0);
        if (remaining > 0) {
          pendingItems += 1;
          pendingQty += remaining;
          pendingAmount += remaining * item.rate;
        }
      });
    });

    return {
      pendingPOs: pendingPOs.length,
      pendingItems,
      pendingQty,
      pendingAmount
    };
  }, [purchaseOrders]);

  if (!canViewPurchaseOrders) {
    return (
      <Box sx={{ mt: 4, mb: 4, px: 3 }}>
        <Alert severity="error">You don't have permission to view purchase orders.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2, mb: 4, px: isMobile ? 1 : 3 }}>
      <Paper elevation={3} sx={{ p: isMobile ? 2 : 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
          <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Purchase Orders
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search PO, vendor, quality..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" />
              }}
              sx={{ minWidth: isMobile ? '100%' : 260 }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ mr: 0.5 }}>
                Show cancelled
              </Typography>
              <Switch
                size="small"
                checked={showCancelled}
                onChange={(e) => setShowCancelled(e.target.checked)}
              />
            </Box>
            {canAddPurchaseOrders && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenAddDialog}
                size={isMobile ? "medium" : "large"}
              >
                New PO
              </Button>
            )}
          </Box>
        </Box>

        {/* Filter bar */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            label="Design No"
            placeholder="e.g. D-001"
            value={filterDesignNo}
            onChange={(e) => setFilterDesignNo(e.target.value)}
            sx={{ minWidth: 160 }}
            InputProps={{
              endAdornment: filterDesignNo ? (
                <IconButton size="small" onClick={() => setFilterDesignNo('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              ) : undefined
            }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Supplier</InputLabel>
            <Select
              value={filterVendorId}
              label="Supplier"
              onChange={(e) => setFilterVendorId(e.target.value as number | '')}
            >
              <MenuItem value=""><em>All Suppliers</em></MenuItem>
              {vendors.map(v => (
                <MenuItem key={v.id} value={v.id}>
                  {v.registered_name}{v.alias ? ` (${v.alias})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value as PurchaseOrderStatus | '')}
            >
              <MenuItem value=""><em>All Statuses</em></MenuItem>
              <MenuItem value={PurchaseOrderStatus.DRAFT}>Draft</MenuItem>
              <MenuItem value={PurchaseOrderStatus.SENT}>Sent</MenuItem>
              <MenuItem value={PurchaseOrderStatus.PARTIALLY_FULFILLED}>Partially Fulfilled</MenuItem>
              <MenuItem value={PurchaseOrderStatus.FULFILLED}>Fulfilled</MenuItem>
              <MenuItem value={PurchaseOrderStatus.CLOSED}>Closed</MenuItem>
              <MenuItem value={PurchaseOrderStatus.CANCELLED}>Cancelled</MenuItem>
            </Select>
          </FormControl>
          {(filterDesignNo || filterVendorId !== '' || filterStatus !== '') && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={() => {
                setFilterDesignNo('');
                setFilterVendorId('');
                setFilterStatus('');
              }}
            >
              Clear Filters
            </Button>
          )}
        </Box>

        {/* Statistics card */}
        <Box sx={{ mb: 3 }}>
          <Card variant="outlined">
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Number of Pending POs
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {stats.pendingPOs}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Pending Items
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {stats.pendingItems}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Total Qty Pending
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {stats.pendingQty.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Amount of Pending POs
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    ₹{stats.pendingAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {loading && purchaseOrders.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {isMobile ? (
              <Stack spacing={2}>
                {purchaseOrders.map((po) => {
                  const totalAmount = po.items?.reduce((sum, item) => sum + (item.amount || 0), 0) || 0;
                  return (
                    <Card key={po.id} variant="outlined">
                      <CardContent sx={{ pb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">
                              PO Number
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                              {po.po_number}
                            </Typography>
                          </Box>
                          <Chip
                            label={po.status}
                            color={getStatusColor(po.status)}
                            size="small"
                          />
                        </Box>

                        <Box sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Vendor
                          </Typography>
                          <Typography variant="body2">
                            {po.vendor?.registered_name || po.vendor?.alias || '-'}
                          </Typography>
                        </Box>

                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Items
                            </Typography>
                            <Typography variant="body2">
                              {po.items?.length || 0}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Amount
                            </Typography>
                            <Typography variant="body2">
                              ₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Created
                            </Typography>
                            <Typography variant="body2">
                              {format(new Date(po.created_at), 'dd MMM yyyy')}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pb: 1, gap: 0.5 }}>
                        <IconButton size="small" onClick={() => handleViewPurchaseOrder(po.id)} color="primary">
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" title="Print PDF" onClick={() => handlePrintPOById(po.id)}>
                          <PrintIcon fontSize="small" />
                        </IconButton>
                        {canEditPurchaseOrders && (
                          <IconButton size="small" onClick={() => handleOpenEditDialog(po)} color="info">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                        {canDeletePurchaseOrders && (
                          <IconButton size="small" onClick={() => handleDeleteClick(po)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Card>
                  );
                })}
              </Stack>
            ) : (
              <>
                <TableContainer>
                  <Table size="medium">
                    <TableHead>
                      <TableRow>
                        <TableCell>PO Number</TableCell>
                        <TableCell>Vendor</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Designs</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Items</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {purchaseOrders.map((po) => {
                        const totalAmount = po.items?.reduce((sum, item) => sum + (item.amount || 0), 0) || 0;
                        return (
                          <TableRow key={po.id} hover>
                            <TableCell sx={{ fontWeight: 'bold' }}>{po.po_number}</TableCell>
                            <TableCell>{po.vendor?.registered_name || po.vendor?.alias || '-'}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 180 }}>
                                {(po.design_tags || []).slice(0, 3).map(tag => (
                                  <Chip key={tag.design_no} label={tag.design_no} size="small" color="primary" variant="outlined" />
                                ))}
                                {(po.design_tags || []).length > 3 && (
                                  <Chip label={`+${(po.design_tags || []).length - 3}`} size="small" variant="outlined" />
                                )}
                                {(po.design_tags || []).length === 0 && (
                                  <Typography variant="caption" color="text.secondary">—</Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={po.status}
                                color={getStatusColor(po.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">{po.items?.length || 0}</TableCell>
                            <TableCell align="right">₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell>{format(new Date(po.created_at), 'dd MMM yyyy')}</TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                <Tooltip title="View">
                                  <IconButton size="small" onClick={() => handleViewPurchaseOrder(po.id)} color="primary">
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Print PDF">
                                  <IconButton size="small" onClick={() => handlePrintPOById(po.id)}>
                                    <PrintIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {canEditPurchaseOrders && (
                                  <Tooltip title="Edit">
                                    <IconButton size="small" onClick={() => handleOpenEditDialog(po)} color="info">
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {canDeletePurchaseOrders && (
                                  <Tooltip title="Delete">
                                    <IconButton size="small" onClick={() => handleDeleteClick(po)} color="error">
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {total > pageSize && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={Math.ceil(total / pageSize)}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  size={isMobile ? "small" : "medium"}
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Add PO Dialog - Mobile Optimized */}
      <Dialog
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
        fullScreen={isMobile}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{poEditMode === 'edit' ? 'Edit Purchase Order' : 'Create Purchase Order'}</Typography>
          <IconButton onClick={handleCloseAddDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ pb: isMobile ? 10 : 2 }}>
          <Stack spacing={3}>
            {/* Vendor Selection - Searchable Autocomplete (SUPPLIER only) */}
            <Autocomplete
              options={vendors}
              getOptionLabel={(v) => v.registered_name + (v.alias ? ` (${v.alias})` : '')}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              value={vendors.find(v => v.id === formData.vendor_id) || null}
              onChange={(_e, newVal) => handleFormChange('vendor_id', newVal ? newVal.id : 0)}
              inputValue={vendorInputValue}
              onInputChange={(_e, val) => setVendorInputValue(val)}
              filterOptions={(opts, state) => {
                const q = state.inputValue.toLowerCase();
                return opts.filter(v =>
                  v.registered_name.toLowerCase().includes(q) ||
                  (v.alias || '').toLowerCase().includes(q) ||
                  (v.phone || '').includes(q)
                );
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{option.registered_name}</Typography>
                    {option.alias && <Typography variant="caption" color="text.secondary">{option.alias}</Typography>}
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Vendor *"
                  error={!!formErrors.vendor_id}
                  helperText={formErrors.vendor_id || 'Only SUPPLIER type vendors shown'}
                  placeholder="Search vendor..."
                />
              )}
            />

            {/* Valid Until - Auto-set to 45 days */}
            <TextField
              fullWidth
              label="Valid Until"
              type="date"
              value={formData.valid_until}
              onChange={(e) => handleFormChange('valid_until', e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Order validity (default: 45 days from today)"
            />

            {/* Design Tags — searchable with thumbnails */}
            <Autocomplete
              multiple
              size="small"
              options={designTagOptions}
              value={designTagOptions.filter(opt => (formData.design_nos || []).includes(opt.design_no))}
              onChange={(_e, newVal) => setFormData(prev => ({ ...prev, design_nos: newVal.map((v: any) => v.design_no) }))}
              loading={designTagsLoading}
              onInputChange={(_e, val, reason) => {
                if (reason === 'input') {
                  if (designTagTimer.current) clearTimeout(designTagTimer.current);
                  designTagTimer.current = setTimeout(() => loadDesignTagOptions(val), 350);
                }
              }}
              getOptionLabel={(opt: any) => opt.design_no + (opt.name ? ` – ${opt.name}` : '')}
              isOptionEqualToValue={(opt: any, val: any) => opt.design_no === val.design_no}
              filterOptions={(opts) => opts}
              renderOption={(props, option: any) => (
                <Box component="li" {...props} key={option.design_no} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}>
                  {option.image_url ? (
                    <Box
                      component="img"
                      src={option.image_url}
                      alt={option.design_no}
                      sx={{ width: 40, height: 52, objectFit: 'cover', borderRadius: 0.5, border: '1px solid', borderColor: 'divider', flexShrink: 0 }}
                    />
                  ) : (
                    <Box sx={{ width: 40, height: 52, bgcolor: 'grey.100', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>No img</Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{option.design_no}</Typography>
                    {option.name && <Typography variant="caption" color="text.secondary">{option.name}</Typography>}
                  </Box>
                </Box>
              )}
              renderTags={(vals: any[], getTagProps) =>
                vals.map((val: any, i) => (
                  <Chip
                    {...getTagProps({ index: i })}
                    key={val.design_no}
                    label={val.design_no}
                    size="small"
                    color="primary"
                    variant="outlined"
                    avatar={val.image_url ? (
                      <Box component="img" src={val.image_url} sx={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover' }} />
                    ) : undefined}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Design Tags (optional)"
                  helperText="Tag approved designs this PO is for"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {designTagsLoading && <CircularProgress size={14} sx={{ mr: 1 }} />}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />

            {/* Items Grid - ERP style */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" color="primary">Items *</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={handleAddItemRow} variant="outlined">
                  Add Row
                </Button>
              </Box>
              <TableContainer sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small" sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 700, width: 32, p: '4px 8px' }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700, minWidth: 110, p: '4px 8px' }}>Type *</TableCell>
                      <TableCell sx={{ fontWeight: 700, minWidth: 150, p: '4px 8px' }}>Quality</TableCell>
                      <TableCell sx={{ fontWeight: 700, minWidth: 160, p: '4px 8px' }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 700, minWidth: 80, p: '4px 8px' }}>Qty *</TableCell>
                      <TableCell sx={{ fontWeight: 700, minWidth: 72, p: '4px 8px' }}>UOM *</TableCell>
                      <TableCell sx={{ fontWeight: 700, minWidth: 80, p: '4px 8px' }}>Rate *</TableCell>
                      <TableCell sx={{ fontWeight: 700, minWidth: 80, p: '4px 8px' }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 700, minWidth: 68, p: '4px 8px' }}>Width</TableCell>
                      <TableCell sx={{ fontWeight: 700, minWidth: 60, p: '4px 8px' }}>L</TableCell>
                      <TableCell sx={{ fontWeight: 700, minWidth: 110, p: '4px 8px' }}>Garment Part</TableCell>
                      <TableCell sx={{ fontWeight: 700, minWidth: 60, p: '4px 8px' }}>Image</TableCell>
                      <TableCell sx={{ width: 36, p: '4px 4px' }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.items.map((item, index) => (
                      <TableRow key={index} sx={{ '& td': { p: '3px 6px', verticalAlign: 'middle' } }}>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem', textAlign: 'center' }}>
                          {index + 1}
                        </TableCell>
                        <TableCell>
                          <FormControl fullWidth size="small" sx={{ minWidth: 100 }}>
                            <Select
                              value={item.material_type}
                              onChange={(e) => handleItemChange(index, 'material_type', e.target.value)}
                              sx={{ fontSize: '0.8rem' }}
                            >
                              <MenuItem value={MaterialType.FABRIC}>Fabric</MenuItem>
                              <MenuItem value={MaterialType.LACE}>Lace</MenuItem>
                              <MenuItem value={MaterialType.BUTTON}>Button</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <Autocomplete
                            freeSolo
                            size="small"
                            options={materialQualityOptions}
                            value={item.material_quality || ''}
                            onInputChange={(_e, val) => handleItemChange(index, 'material_quality', val)}
                            renderInput={(params) => (
                              <TextField {...params} placeholder="Quality" sx={{ '& input': { fontSize: '0.8rem' } }} />
                            )}
                            sx={{ minWidth: 140 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth size="small" multiline maxRows={2}
                            placeholder="Description"
                            value={item.description || ''}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            sx={{ minWidth: 150, '& textarea, & input': { fontSize: '0.8rem' } }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small" type="number"
                            value={item.quantity || ''}
                            onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value))}
                            error={!!formErrors[`item_${index}_quantity`]}
                            inputProps={{ step: '0.01', min: 0, style: { fontSize: '0.8rem', width: 68 } }}
                          />
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 64 }}>
                            <Select
                              value={item.uom}
                              onChange={(e) => handleItemChange(index, 'uom', e.target.value)}
                              sx={{ fontSize: '0.8rem' }}
                            >
                              {MATERIAL_UOMS.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small" type="number"
                            value={item.rate || ''}
                            onChange={(e) => handleItemChange(index, 'rate', parseFloat(e.target.value))}
                            error={!!formErrors[`item_${index}_rate`]}
                            inputProps={{ step: '0.01', min: 0, style: { fontSize: '0.8rem', width: 68 } }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={item.amount?.toFixed(2) || '0.00'}
                            InputProps={{ readOnly: true }}
                            inputProps={{ style: { fontSize: '0.8rem', width: 68, color: '#666' } }}
                          />
                        </TableCell>
                        <TableCell>
                          {item.material_type === MaterialType.FABRIC ? (
                            <TextField
                              size="small" type="number"
                              value={item.fabric_width || ''}
                              onChange={(e) => handleItemChange(index, 'fabric_width', parseFloat(e.target.value))}
                              inputProps={{ step: '0.01', min: 0, style: { fontSize: '0.8rem', width: 60 } }}
                            />
                          ) : <Box sx={{ width: 60 }} />}
                        </TableCell>
                        <TableCell>
                          {item.material_type === MaterialType.FABRIC ? (
                            <TextField
                              size="small" type="number"
                              value={item.fabric_L || ''}
                              onChange={(e) => handleItemChange(index, 'fabric_L', parseFloat(e.target.value))}
                              inputProps={{ step: '0.01', min: 0, style: { fontSize: '0.8rem', width: 54 } }}
                            />
                          ) : <Box sx={{ width: 54 }} />}
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select
                              displayEmpty
                              value={item.garment_part || ''}
                              onChange={(e) => handleItemChange(index, 'garment_part', e.target.value || null)}
                              sx={{ fontSize: '0.8rem' }}
                            >
                              <MenuItem value=""><em style={{ fontSize: '0.8rem', color: '#999' }}>Any</em></MenuItem>
                              <MenuItem value="TOP">TOP</MenuItem>
                              <MenuItem value="DUPATTA">DUPATTA</MenuItem>
                              <MenuItem value="BOTTOM">BOTTOM</MenuItem>
                              <MenuItem value="JACKET">JACKET</MenuItem>
                              <MenuItem value="BELT">BELT</MenuItem>
                              <MenuItem value="OTHER">OTHER</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <input
                              ref={(el) => { fileInputRefs.current[index] = el; }}
                              type="file" accept="image/*"
                              style={{ display: 'none' }}
                              onChange={(e) => handleFileSelect(index, e)}
                            />
                            {item.image_url ? (
                              <Box
                                component="img"
                                src={item.image_url}
                                alt={`item-${index}`}
                                sx={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 0.5, cursor: 'pointer', flexShrink: 0 }}
                                onClick={() => fileInputRefs.current[index]?.click()}
                              />
                            ) : (
                              <IconButton size="small" onClick={() => fileInputRefs.current[index]?.click()}>
                                <PhotoLibraryIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {formData.items.length > 1 && (
                            <IconButton size="small" color="error" onClick={() => handleRemoveItemRow(index)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Notes and Terms - Accordion for better mobile UX */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Additional Details (Optional)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => handleFormChange('notes', e.target.value)}
                    placeholder="Any additional notes for this PO..."
                  />
                  <TextField
                    fullWidth
                    label="Terms"
                    multiline
                    rows={3}
                    value={formData.terms}
                    onChange={(e) => handleFormChange('terms', e.target.value)}
                    placeholder="Payment terms, delivery terms, etc..."
                  />
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </DialogContent>

        <DialogActions sx={{
          position: isMobile ? 'fixed' : 'relative',
          bottom: isMobile ? 0 : 'auto',
          left: isMobile ? 0 : 'auto',
          right: isMobile ? 0 : 'auto',
          bgcolor: 'background.paper',
          borderTop: isMobile ? '1px solid' : 'none',
          borderColor: 'divider',
          p: 2,
          zIndex: 1
        }}>
          <Button onClick={handleCloseAddDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            {loading ? 'Saving...' : poEditMode === 'edit' ? 'Update PO' : 'Create PO'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View PO Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        fullScreen={isMobile}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" noWrap>
                PO #{selectedPurchaseOrder?.po_number || ''}
              </Typography>
              {selectedPurchaseOrder?.vendor && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {selectedPurchaseOrder.vendor.registered_name}
                  {selectedPurchaseOrder.vendor.alias ? ` (${selectedPurchaseOrder.vendor.alias})` : ''}
                </Typography>
              )}
            </Box>
            {selectedPurchaseOrder && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {canEditPurchaseOrders ? (
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={selectedPurchaseOrder.status}
                      label="Status"
                      onChange={(e) => handleStatusChange(e.target.value as PurchaseOrderStatus)}
                    >
                      <MenuItem value={PurchaseOrderStatus.DRAFT}>Draft</MenuItem>
                      <MenuItem value={PurchaseOrderStatus.SENT}>Sent</MenuItem>
                      <MenuItem value={PurchaseOrderStatus.PARTIALLY_FULFILLED}>Partially Fulfilled</MenuItem>
                      <MenuItem value={PurchaseOrderStatus.FULFILLED}>Fulfilled</MenuItem>
                      <MenuItem value={PurchaseOrderStatus.CLOSED}>Closed</MenuItem>
                      <MenuItem value={PurchaseOrderStatus.CANCELLED}>Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <Chip
                    label={selectedPurchaseOrder.status}
                    color={getStatusColor(selectedPurchaseOrder.status)}
                    variant="outlined"
                  />
                )}
                <Button
                  onClick={handlePrintPurchaseOrder}
                  disabled={viewLoading || !selectedPurchaseOrder}
                  startIcon={<PrintIcon />}
                  variant="outlined"
                  size="small"
                >
                  Print PDF
                </Button>
              </Box>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {viewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedPurchaseOrder ? (
            <Stack spacing={3}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Vendor</Typography>
                    <Typography variant="body1">
                      {selectedPurchaseOrder.vendor?.registered_name || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">PO Number</Typography>
                    <Typography variant="body1">{selectedPurchaseOrder.po_number}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Created</Typography>
                    <Typography variant="body1">
                      {format(new Date(selectedPurchaseOrder.created_at), 'dd MMM yyyy, hh:mm a')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Valid Until</Typography>
                    <Typography variant="body1">
                      {selectedPurchaseOrder.valid_until
                        ? format(new Date(selectedPurchaseOrder.valid_until), 'dd MMM yyyy')
                        : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Created By</Typography>
                    <Typography variant="body1">{selectedPurchaseOrder.created_by}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Total Items</Typography>
                    <Typography variant="body1">{selectedPurchaseOrder.items?.length || 0}</Typography>
                  </Grid>
                  {(selectedPurchaseOrder.design_tags || []).length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">Design Tags</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {(selectedPurchaseOrder.design_tags || []).map(tag => (
                          <Chip key={tag.design_no} label={tag.design_no} size="small" color="primary" variant="outlined" />
                        ))}
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                    <Typography variant="body1">
                      {selectedPurchaseOrder.notes || '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Terms</Typography>
                    <Typography variant="body1">
                      {selectedPurchaseOrder.terms || '—'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Items
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Photo</TableCell>
                        <TableCell>Material</TableCell>
                        <TableCell>Garment Part</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>Rate</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedPurchaseOrder.items?.map((item, index) => {
                        const currentStatus = (item.id !== undefined && itemStatusEdits[item.id] !== undefined)
                          ? itemStatusEdits[item.id]
                          : (item.status as POItemStatus) || POItemStatus.PENDING;
                        const isEdited = item.id !== undefined && itemStatusEdits[item.id] !== undefined;
                        return (
                        <TableRow
                          key={item.id || index}
                          sx={isEdited ? { bgcolor: 'action.hover' } : undefined}
                        >
                          <TableCell>
                            {item.image_url ? (
                              <Box
                                component="img"
                                src={item.image_url}
                                alt={`Item ${index + 1}`}
                                sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}
                              />
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                No image
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item.material_type}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {item.material_quality || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {item.garment_part ? (
                              <Chip
                                label={item.garment_part}
                                size="small"
                                color={item.garment_part === 'DUPATTA' ? 'secondary' : item.garment_part === 'TOP' ? 'primary' : 'default'}
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            ) : (
                              <Typography variant="caption" color="text.disabled">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {item.quantity} {item.uom}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              ₹{item.rate.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              ₹{item.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {canEditPurchaseOrders && item.id !== undefined ? (
                              <FormControl size="small" sx={{ minWidth: 160 }}>
                                <Select
                                  value={currentStatus}
                                  onChange={(e) => handleItemStatusEdit(item.id!, e.target.value as POItemStatus)}
                                  sx={{
                                    fontSize: '0.8rem',
                                    ...(isEdited && {
                                      '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'warning.main',
                                        borderWidth: 2
                                      }
                                    })
                                  }}
                                >
                                  <MenuItem value={POItemStatus.PENDING}>
                                    <Chip label="PENDING" size="small" color="default" sx={{ pointerEvents: 'none' }} />
                                  </MenuItem>
                                  <MenuItem value={POItemStatus.PARTIALLY_FULFILLED}>
                                    <Chip label="PARTIALLY FULFILLED" size="small" color="warning" sx={{ pointerEvents: 'none' }} />
                                  </MenuItem>
                                  <MenuItem value={POItemStatus.FULFILLED}>
                                    <Chip label="FULFILLED" size="small" color="success" sx={{ pointerEvents: 'none' }} />
                                  </MenuItem>
                                </Select>
                              </FormControl>
                            ) : (
                              <Chip
                                label={item.status}
                                size="small"
                                color={
                                  item.status === 'FULFILLED'
                                    ? 'success'
                                    : item.status === 'PARTIALLY_FULFILLED'
                                      ? 'warning'
                                      : 'default'
                                }
                              />
                            )}
                          </TableCell>
                        </TableRow>
                        );
                      })}
                      {(!selectedPurchaseOrder.items || selectedPurchaseOrder.items.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography variant="body2" color="text.secondary">
                              No items
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Stack>
          ) : (
            <Typography>No purchase order selected.</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ gap: 1 }}>
          {canEditPurchaseOrders && Object.keys(itemStatusEdits).length > 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveItemStatuses}
              disabled={savingItemStatuses}
              startIcon={savingItemStatuses ? <CircularProgress size={18} /> : <CheckIcon />}
            >
              {savingItemStatuses ? 'Saving...' : `Save Item Statuses (${Object.keys(itemStatusEdits).length})`}
            </Button>
          )}
          <Button onClick={handleCloseViewDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete PO <strong>{poToDelete?.po_number}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Camera Capture Dialog */}
      <Dialog
        open={captureDialogOpen}
        onClose={handleCloseCamera}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Take Photo</Typography>
            <IconButton onClick={handleCloseCamera} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ position: 'relative', width: '100%', aspectRatio: '4/3', bgcolor: 'black', borderRadius: 1, overflow: 'hidden' }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', p: 2 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleCapturePhoto}
            startIcon={<CameraIcon />}
            sx={{ minWidth: 150 }}
          >
            Capture
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurchaseOrdersPage;
